import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import posthog, { setAnalyticsConsent } from '../../posthog';
import { useConsent } from '../../consent/ConsentContext';

/**
 * Bridges consent → analytics side-effects for the dashboard (GDPR plan P2-1):
 *   - PostHog opt-in / opt-out. PostHog is init'd with
 *     `opt_out_capturing_by_default: true`, so it captures NOTHING (no
 *     autocapture, no pageview) until analytics consent is granted here.
 *   - Manual `$pageview` capture on first grant + every route change, but ONLY
 *     while analytics consent is granted (no pre-consent buffering).
 *   - Bridges the existing `tc.*` analytics façade (window 'tc:analytics'
 *     CustomEvent) → PostHog, also gated.
 *
 * Vercel <Analytics/> and the Cloudflare/Supademo scripts are gated by
 * conditional render in _app.js / ConsentedScripts.jsx, not here.
 */
export default function AnalyticsConsentGate() {
  const router = useRouter();
  const { ready, has } = useConsent();
  const analytics = has('analytics');

  // Apply opt-in/out whenever the analytics decision changes (also on first
  // hydrate, which opts out explicitly if no consent was stored).
  useEffect(() => {
    if (!ready) return;
    setAnalyticsConsent(analytics);
  }, [ready, analytics]);

  // First pageview once consent is granted — only once per page load, even if
  // consent is toggled off and back on without a navigation (route-change
  // pageviews are handled by the effect below).
  const firstPageviewSent = useRef(false);
  useEffect(() => {
    if (ready && analytics && !firstPageviewSent.current) {
      posthog.capture('$pageview');
      firstPageviewSent.current = true;
    }
  }, [ready, analytics]);

  // Subsequent SPA pageviews (Pages Router fires no history hook) — only while
  // analytics consent is granted.
  useEffect(() => {
    if (!analytics) return;
    const onRouteChange = () => posthog.capture('$pageview');
    router.events.on('routeChangeComplete', onRouteChange);
    return () => router.events.off('routeChangeComplete', onRouteChange);
  }, [analytics, router.events]);

  // Bridge tc.* façade events → PostHog (only while analytics granted).
  useEffect(() => {
    if (!analytics) return;
    const onTc = (e) => {
      const { name, properties } = e.detail || {};
      if (name) posthog.capture(name, properties);
    };
    window.addEventListener('tc:analytics', onTc);
    return () => window.removeEventListener('tc:analytics', onTc);
  }, [analytics]);

  return null;
}
